import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reviewService } from '../../../services/reviewService';
import { useAuth } from '../../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const ReviewsSection = ({ eventId, eventStatus }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: []
  });
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    comment: ''
  });

  const displayedReviews = showAll ? reviews : reviews?.slice(0, 3);

  useEffect(() => {
    loadReviewsData();
  }, [eventId]);

  const loadReviewsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load reviews and stats
      const [reviewsData, statsData] = await Promise.all([
        reviewService?.getEventReviews(eventId),
        reviewService?.getEventReviewStats(eventId)
      ]);

      setReviews(reviewsData);
      setStats(statsData);

      // Check if user can review
      if (user && eventStatus === 'completed') {
        const canUserReview = await reviewService?.canUserReview(eventId);
        setCanReview(canUserReview);

        // Check if user already reviewed
        const existingReview = await reviewService?.getUserReview(eventId);
        setUserReview(existingReview);
        if (existingReview) {
          setFormData({
            rating: existingReview?.rating,
            comment: existingReview?.comment
          });
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(err?.message);
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e?.preventDefault();
    if (!formData?.comment?.trim()) {
      setError('Lütfen bir yorum yazın');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (userReview) {
        // Update existing review
        await reviewService?.updateEventReview(
          userReview?.id,
          formData?.rating,
          formData?.comment
        );
      } else {
        // Create new review
        await reviewService?.createEventReview(
          eventId,
          formData?.rating,
          formData?.comment
        );
      }

      // Reload reviews
      await loadReviewsData();
      setShowReviewForm(false);
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message);
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!userReview || !window.confirm('Değerlendirmenizi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setSubmitting(true);
      await reviewService?.deleteEventReview(userReview?.id);
      await loadReviewsData();
      setSubmitting(false);
    } catch (err) {
      console.error('Error deleting review:', err);
      setError(err?.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          Değerlendirmeler
        </h3>
        {canReview && !userReview && (
          <Button
            onClick={() => setShowReviewForm(!showReviewForm)}
            size="sm"
            iconName={showReviewForm ? 'X' : 'Plus'}
          >
            {showReviewForm ? 'İptal' : 'Değerlendirme Yap'}
          </Button>
        )}
        {userReview && (
          <div className="flex gap-2">
            <Button
              onClick={() => setShowReviewForm(!showReviewForm)}
              size="sm"
              variant="outline"
              iconName="Edit"
            >
              Düzenle
            </Button>
            <Button
              onClick={handleDeleteReview}
              size="sm"
              variant="destructive"
              iconName="Trash2"
              disabled={submitting}
            >
              Sil
            </Button>
          </div>
        )}
      </div>
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {showReviewForm && (
        <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-muted/50 rounded-lg">
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Puan
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="transition-transform hover:scale-110"
                >
                  <Icon
                    name="Star"
                    size={32}
                    className={`${
                      star <= formData?.rating
                        ? 'text-warning fill-warning' :'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Yorumunuz
            </label>
            <textarea
              value={formData?.comment}
              onChange={(e) => setFormData({ ...formData, comment: e?.target?.value })}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows="4"
              placeholder="Etkinlik hakkındaki düşüncelerinizi paylaşın..."
              required
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowReviewForm(false)}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : userReview ? 'Güncelle' : 'Gönder'}
            </Button>
          </div>
        </form>
      )}
      {stats?.totalReviews > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-1">
              <div className="text-center p-6 bg-muted rounded-lg">
                <div className="text-5xl font-bold text-foreground mb-2">
                  {stats?.averageRating}
                </div>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Icon
                      key={star}
                      name="Star"
                      size={20}
                      className={`${
                        star <= Math.round(stats?.averageRating)
                          ? 'text-warning fill-warning' :'text-muted-foreground'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  {stats?.totalReviews} değerlendirme
                </p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="space-y-2">
                {stats?.ratingDistribution?.map((item) => (
                  <div key={item?.stars} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-16">
                      <span className="text-sm font-medium text-foreground">
                        {item?.stars}
                      </span>
                      <Icon name="Star" size={14} className="text-warning fill-warning" />
                    </div>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-warning rounded-full transition-all"
                        style={{ width: `${item?.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-12 text-right">
                      {item?.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {displayedReviews?.map((review) => (
              <div key={review?.id} className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <Link to={`/profile/${review?.userId}`} className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-muted hover:ring-2 hover:ring-primary transition-all">
                    {review?.userAvatar ? (
                      <Image
                        src={review?.userAvatar}
                        alt={review?.userAvatarAlt}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <Link to={`/profile/${review?.userId}`} className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                        {review?.userName}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {review?.date}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5]?.map((star) => (
                        <Icon
                          key={star}
                          name="Star"
                          size={14}
                          className={`${
                            star <= review?.rating
                              ? 'text-warning fill-warning' :'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground leading-relaxed">
                  {review?.comment}
                </p>
              </div>
            ))}
          </div>

          {reviews?.length > 3 && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                onClick={() => setShowAll(!showAll)}
                iconName={showAll ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
              >
                {showAll ? 'Daha Az Göster' : `Tüm Değerlendirmeleri Gör (${reviews?.length})`}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Henüz değerlendirme yapılmamış
          </p>
          {canReview && !userReview && (
            <p className="text-sm text-muted-foreground mt-2">
              İlk değerlendirmeyi siz yapın!
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;